<div class="user-management-container">
    <div class="header">
        <h1>User Management</h1>
        <p class="subtitle">Manage users, roles, and permissions</p>
    </div>

    <!-- Success/Error Messages -->
    <div *ngIf="successMessage" class="alert alert-success">
        {{ successMessage }}
    </div>
    <div *ngIf="errorMessage" class="alert alert-error">
        {{ errorMessage }}
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="loading-container">
        <div class="spinner"></div>
        <p>Loading users...</p>
    </div>

    <!-- Users Table -->
    <div *ngIf="!loading" class="table-container">
        <div class="table-header">
            <div class="table-info">
                <span>Total Users: {{ totalUsers }}</span>
                <span>Page {{ currentPage }} of {{ totalPages }}</span>
            </div>
        </div>

        <table class="users-table">
            <thead>
                <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Type</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Last Login</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                <tr *ngFor="let user of users" class="user-row">
                    <td>
                        <div class="user-info">
                            <div class="user-name">{{ user.first_name }} {{ user.last_name }}</div>
                            <div class="user-title" *ngIf="user.business_title">{{ user.business_title }}</div>
                        </div>
                    </td>
                    <td>{{ user.email }}</td>
                    <td>
                        <span class="user-type-badge" [class.business]="user.user_type === 'Agency'">
                            {{ getUserTypeDisplay(user.user_type) }}
                        </span>
                    </td>
                    <td>
                        <span class="role-badge" [class.admin]="user.role === 'Admin'">
                            {{ getRoleDisplayName(user.role) }}
                        </span>
                    </td>
                    <td>
                        <span class="status-badge" [class.active]="user.is_active" [class.inactive]="!user.is_active">
                            {{ user.is_active ? 'Active' : 'Inactive' }}
                        </span>
                    </td>
                    <td>{{ user.last_login ? formatDate(user.last_login) : 'Never' }}</td>
                    <td>
                        <div class="action-buttons">
                            <button class="btn btn-sm btn-primary" (click)="openEditModal(user)">
                                Edit
                            </button>
                            <button class="btn btn-sm btn-secondary" (click)="openRoleModal(user)">
                                Change Role
                            </button>
                            <button class="btn btn-sm btn-info" (click)="openResetPasswordModal(user)">
                                Reset Password
                            </button>
                            <button class="btn btn-sm" [class.btn-success]="!user.is_active"
                                [class.btn-warning]="user.is_active" (click)="openStatusModal(user)">
                                {{ user.is_active ? 'Deactivate' : 'Activate' }}
                            </button>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>

        <!-- Pagination -->
        <div class="pagination" *ngIf="totalPages > 1">
            <button class="btn btn-sm" [disabled]="currentPage === 1" (click)="onPageChange(currentPage - 1)">
                Previous
            </button>

            <span class="page-info">
                Page {{ currentPage }} of {{ totalPages }}
            </span>

            <button class="btn btn-sm" [disabled]="currentPage === totalPages" (click)="onPageChange(currentPage + 1)">
                Next
            </button>
        </div>
    </div>

    <!-- Edit User Modal -->
    <div *ngIf="showEditModal" class="modal-overlay" (click)="closeModals()">
        <div class="modal" (click)="$event.stopPropagation()">
            <div class="modal-header">
                <h3>Edit User</h3>
                <button class="close-btn" (click)="closeModals()">&times;</button>
            </div>

            <div class="modal-body">
                <form (ngSubmit)="updateUser()">
                    <div class="form-group">
                        <label for="first_name">First Name</label>
                        <input type="text" id="first_name" [(ngModel)]="editForm.first_name" name="first_name" required>
                    </div>

                    <div class="form-group">
                        <label for="last_name">Last Name</label>
                        <input type="text" id="last_name" [(ngModel)]="editForm.last_name" name="last_name" required>
                    </div>

                    <div class="form-group">
                        <label for="phone">Phone</label>
                        <input type="tel" id="phone" [(ngModel)]="editForm.phone" name="phone">
                    </div>

                    <div class="form-group" *ngIf="selectedUser?.user_type === 'Agency' || selectedUser?.user_type === 'Company/brand'">
                        <label for="business_title">Business Title</label>
                        <input type="text" id="business_title" [(ngModel)]="editForm.business_title"
                            name="business_title">
                    </div>

                    <div class="modal-actions">
                        <button type="button" class="btn btn-secondary" (click)="closeModals()">Cancel</button>
                        <button type="submit" class="btn btn-primary" [disabled]="saving">
                            {{ saving ? 'Saving...' : 'Save Changes' }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>

    <!-- Change Role Modal -->
    <div *ngIf="showRoleModal" class="modal-overlay" (click)="closeModals()">
        <div class="modal" (click)="$event.stopPropagation()">
            <div class="modal-header">
                <h3>Change User Role</h3>
                <button class="close-btn" (click)="closeModals()">&times;</button>
            </div>

            <div class="modal-body">
                <form (ngSubmit)="changeUserRole()">
                    <div class="form-group">
                        <label for="role">Role</label>
                        <select id="role" [(ngModel)]="roleForm.role" name="role" required>
                            <option *ngFor="let role of roles" [value]="role.name">
                                {{ role.description }}
                            </option>
                        </select>
                    </div>

                    <div class="modal-actions">
                        <button type="button" class="btn btn-secondary" (click)="closeModals()">Cancel</button>
                        <button type="submit" class="btn btn-primary" [disabled]="saving">
                            {{ saving ? 'Updating...' : 'Update Role' }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>

    <!-- Toggle Status Modal -->
    <div *ngIf="showStatusModal" class="modal-overlay" (click)="closeModals()">
        <div class="modal" (click)="$event.stopPropagation()">
            <div class="modal-header">
                <h3>{{ statusForm.is_active ? 'Deactivate' : 'Activate' }} User</h3>
                <button class="close-btn" (click)="closeModals()">&times;</button>
            </div>

            <div class="modal-body">
                <p>Are you sure you want to {{ statusForm.is_active ? 'deactivate' : 'activate' }}
                    <strong>{{ selectedUser?.first_name }} {{ selectedUser?.last_name }}</strong>?
                </p>

                <div class="modal-actions">
                    <button type="button" class="btn btn-secondary" (click)="closeModals()">Cancel</button>
                    <button type="button" class="btn" [class.btn-warning]="statusForm.is_active"
                        [class.btn-success]="!statusForm.is_active" [disabled]="saving" (click)="toggleUserStatus()">
                        {{ saving ? 'Updating...' : (statusForm.is_active ? 'Deactivate' : 'Activate') }}
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Reset Password Modal -->
    <div *ngIf="showResetPasswordModal" class="modal-overlay" (click)="closeModals()">
        <div class="modal" (click)="$event.stopPropagation()">
            <div class="modal-header">
                <h3>Reset Password</h3>
                <button class="close-btn" (click)="closeModals()">&times;</button>
            </div>

            <div class="modal-body">
                <div *ngIf="!resetPasswordResponse">
                    <p>Are you sure you want to reset the password for 
                        <strong>{{ selectedUser?.first_name }} {{ selectedUser?.last_name }}</strong> ({{ selectedUser?.email }})?
                    </p>
                    <p class="warning-text">A temporary password will be generated. Please share it with the user.</p>

                    <div class="modal-actions">
                        <button type="button" class="btn btn-secondary" (click)="closeModals()">Cancel</button>
                        <button type="button" class="btn btn-primary" [disabled]="saving" (click)="resetPassword()">
                            {{ saving ? 'Resetting...' : 'Reset Password' }}
                        </button>
                    </div>
                </div>

                <div *ngIf="resetPasswordResponse" class="reset-password-result">
                    <div class="success-message">
                        <p><strong>Password reset successfully!</strong></p>
                        <p>Please copy and share the following information with the user:</p>
                    </div>

                    <div class="password-info">
                        <div class="info-row">
                            <label>Email:</label>
                            <div class="value-with-copy">
                                <span class="value">{{ resetPasswordResponse.email }}</span>
                                <button class="btn btn-sm btn-copy" (click)="copyToClipboard(resetPasswordResponse.email)">
                                    <mat-icon>content_copy</mat-icon>
                                </button>
                            </div>
                        </div>
                        <div class="info-row">
                            <label>Temporary Password:</label>
                            <div class="value-with-copy">
                                <span class="value password-value">{{ resetPasswordResponse.temporary_password }}</span>
                                <button class="btn btn-sm btn-copy" (click)="copyToClipboard(resetPasswordResponse.temporary_password)">
                                    <mat-icon>content_copy</mat-icon>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="modal-actions">
                        <button type="button" class="btn btn-primary" (click)="closeModals()">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
